// ValidationError is { node: DataSourceNode, error: String, showDialog: Boolean, dialogError: String }

import { chain, compose, curry, invoker, reject } from 'ramda';
import { getNodeData, setNodeData } from '../utility/nodeDataHelpers';

export const VALIDATION_MODE_NONE = 'none';
export const VALIDATION_MODE_AUTO = 'auto';
export const VALIDATION_MODE_AFTER_CHANGE = 'afterChange';
export const VALIDATION_MODE_AFTER_ERROR_CHANGE = 'afterErrorChange';

const ERROR_KEY = '__fv__validation_error';

// String -> DataSourceNode -> void
const setErrorToNode = (error, node) => setNodeData(
    node.getNode(),
    ERROR_KEY,
    error,
);

// DOMNode -> String
export const nodeErrorMessage = (node) =>
    (node && getNodeData(node, ERROR_KEY)) || '';

// DataSourceNode -> Boolean
const hasError = (node) =>
    !!(node && nodeErrorMessage(node.getNode()));


const validationError = (node, error, ruleType, dialogError) => ({
    node,
    error,
    showDialog: ruleType === 'modal',
    dialogError,
});

function validation(dataSources, template) {
    // String -> DataSourceNode -> [DataSourceNode]
    const selectNodes = invoker(1, 'selectNodes');

    // [ValidationError]
    const errors = [];

    let validationMode = VALIDATION_MODE_AFTER_ERROR_CHANGE;

    // () -> void
    function clearErrors() {
        for (const { node } of errors) {
            setErrorToNode('', node);
        }

        errors.length = 0;
    }

    // ValidationRule -> DataSourceNode -> void
    function setError(rule, node) {
        const { message, shortMessage, type } = rule.errorMessage || {};
        const errorString = shortMessage || message || 'Unknown error';

        setErrorToNode(errorString, node);

        errors.push(validationError(node, errorString, type, message));
    }

    // ValidationRule -> DataSourceNode -> void
    const validateNode = curry((rule, contextNode) => {
        const isError = contextNode.evaluateBoolean(rule.expression);

        if (isError) {
            setError(rule, contextNode);
        }
    });

    // ValidationRule -> DataSourceNode -> [DataSourceNode]
    const getContextNodes = (contextPath) => (matchNode) =>
        contextPath && contextPath !== '.'
            ? selectNodes(contextPath, matchNode)
            : [matchNode];

    // ValidationRule -> DataSource -> [DataSourceNode]
    const getNodesToValidate = ({ match, expressionContext }) =>
        compose(
            reject(hasError),
            chain(getContextNodes(expressionContext)),
            selectNodes(match),
        );

    // ValidationRule -> DataSource -> void
    const processValidationRule = (rule, dataSource) => {
        for (const node of getNodesToValidate(rule)(dataSource)) {
            validateNode(rule, node);
        }
    };

    // () -> void
    const validateAll = () => {
        const dataSource = dataSources.getDom('')

        clearErrors();

        for (const rule of template.getAllValidationRules()) {
            processValidationRule(rule, dataSource);
        }
    };

    // Boolean? - > [ValidationError]
    const getErrors = (revalidate) => {
        if (revalidate) {
            validateAll();
        }

        return errors.slice();
    };

    function setMode(mode) {
        switch (mode) {
            case VALIDATION_MODE_AUTO:
            case VALIDATION_MODE_AFTER_CHANGE:
            case VALIDATION_MODE_AFTER_ERROR_CHANGE:
            case VALIDATION_MODE_NONE:
                validationMode = mode;
                return;
            default:
                throw new Error(`Invalid validation mode: ${mode}`);
        }
    }

    const getMode = () => validationMode;

    return {
        validateAll,
        clearErrors,
        getErrors,
        setMode,
        getMode,
    };
}

export default validation;